import {useDrag, useDrop} from "react-dnd";
import {runInAction} from "mobx";
import update from "immutability-helper";
import {SetStateAction, useContext, useEffect, useRef} from "react";
import {StoreContext} from "../../../stores/StoreLoader";
import {IMicrositeLink, useMicrosite} from "./Microsite";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";
import FontAwesome from "../../utilities/FontAwesome";

const DRAG_TYPE = "MICROSITE-NAV";

type IDragItem = {
    type: string,
    link: IMicrositeLink,
    index: number,
}

// used to recursively find an item given a list of items where each might contain children that could hold that item
// when microsite navs get re-ordered, this is important so we're writing a grid order based only on the level the items
// being reordered are at
export function findAffectedList(list, affectedItemId) {
    for (let i = 0; i < list.length; i++) {
        const current = list[i];
        if (current.id === affectedItemId) {
            return list;
        } else if (current.children?.length > 0) {
            const affected = findAffectedList(current.children, affectedItemId);
            if (affected) {
                return affected;
            }
        }
    }
}

export function DraggableMicrositeNavigation(props: {
    link: IMicrositeLink,
    index: number,
    isDragging?: boolean,
    setIsDragging: SetStateAction<any>,
}) {
    const {organizationStore, userStore} = useContext(StoreContext);

    const ref = useRef<HTMLAnchorElement>(null);
    const {subNavigation} = useMicrosite();

    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        hover(item: IDragItem, monitor) {
            if (!ref.current) return;

            const dragIndex = item.index;
            const hoverIndex = props.index;

            const draggedItemId = item.link.id;
            const hoveredItemId = props.link.id;

            // Don't replace items with themselves
            if (draggedItemId === hoveredItemId) return;
            // Determine rectangle on screen
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            runInAction(() => {
                // recursively find item from sub navigations
                let list = findAffectedList(subNavigation, draggedItemId);
                const dropTargetIndex = list.findIndex(l => l.id === hoveredItemId);
                const dragTargetIndex = list.findIndex(l => l.id === draggedItemId);
                list = update(list, {
                    $splice: [
                        [dragTargetIndex, 1],
                        [dropTargetIndex, 0, list[dragTargetIndex]],
                    ],
                });
                for (let n = 0; n < list.length; n++) {
                    /* rather than adding a whole new column for this one use-case, set grid_sort_order to over 100, so
                    sections will just be re-ordered at the end of the page if microsite is turned off. the reason we can't
                    just swap sort orders of blocks is to handle sub-blocks where we don't know how many blocks are on
                    the page. */
                    list[n].grid_sort_order = n + 100;
                }
                organizationStore.currentOrganization.microsite.navigation = organizationStore.currentOrganization.microsite.navigation
                    .map(n => {
                        const idx = list.findIndex(l => l.id === n.model.id);
                        return {
                            ...n,
                            model: {
                                ...n.model,
                                grid_sort_order: idx > -1 ? list[idx].grid_sort_order : n.model.grid_sort_order,
                            }
                        }
                    })
                    .sort((a,b) => {
                        // don't reorder the first item, we depend on that item being the top level microsite
                        if (a.model.id === organizationStore.currentOrganization.microsite.id || b.model.id === organizationStore.currentOrganization.microsite.id) {
                            return 0;
                        }
                        return a.model.grid_sort_order - b.model.grid_sort_order;
                    });
            })
        },
    });

    const [{isDragging}, drag] = useDrag({
        item: {type: DRAG_TYPE, link: props.link, index: props.index},
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        async end(item: IDragItem, monitor) {
            const reorderedList = findAffectedList(subNavigation, item.link.id);
            if (reorderedList && reorderedList.length > 0) {
                reorderedList.forEach((item) => {
                    if (!item.model.user_id) {
                        item.model.user_id = userStore.id;
                        item.model.published = 1;
                        item.model.logo = '';
                    }
                })
                try {
                    const client = await WatsonApi();
                    await client.apis.organizations.organizations_blocks_bulk_update({
                        organization_pk: reorderedList[0].model.parent_id,
                        data: reorderedList.map(n => ({
                            ...n.model,
                            grid_sort_order: n.model.grid_sort_order,
                        })),
                    });

                    NotificationManager.success("Microsite successfully updated.");
                } catch (e) {
                    NotificationManager.error("Oops! Something went wrong. Please try again.");
                }
            }

        }
    });

    useEffect(() => {
        props.setIsDragging(isDragging);
    }, [isDragging]);

    useEffect(() => {
        drag(drop(ref));
    }, [])

    return <a
        href={"#"}
        ref={ref}
        style={{opacity: isDragging || props.isDragging ? 0 : 1}}
    >
        <FontAwesome prefix={"fas"} name={"fas fa-arrows-alt"}/>
        <span>{props.link.label}</span>
        <FontAwesome name={'fa-angle-double-right'} prefix={'fas'}/>
    </a>
}
